<script lang="ts" setup>
import { inject, nextTick, onMounted, onUnmounted, ref } from 'vue'
import type { Ref } from 'vue'
import { useGptAdManager } from '../composables/useGptAdManager'
import { createDebugger } from '@/utils/debug'
import { pushAxonEvent } from '@/utils/axonTrack'

const props = defineProps<{
  adSlotIndex: number
  adInstanceId: string
  adOrder: number
  chapterId: string
}>()

const debug = createDebugger('ad')

const {
  getAdSlotForIndex,
  registerPreloadItem,
  waitForPreloadTurn,
  startPreloadIfNeeded,
  noteAdVisible,
  noteAdFailed,
  displayAd,
  destroyAd,
  AD_ENABLED,
  adSize,
  PRELOAD_BATCH_SIZE,
} = useGptAdManager()
const containerRef = ref<HTMLElement>()
const readerContainerRef = inject<Ref<HTMLElement | undefined>>('containerRef')

const globalPreloadOrder = registerPreloadItem()
const preloadBatch = Math.ceil(globalPreloadOrder / PRELOAD_BATCH_SIZE)
const divId = `gpt-storyella-${props.adSlotIndex}-${props.adInstanceId.replace(/[^a-zA-Z0-9_-]/g, '-')}`
const slotConfig = getAdSlotForIndex(props.adSlotIndex)
const isAdAvailable = AD_ENABLED && !!slotConfig
const unitPath = slotConfig?.unitPath || ''
const slotName = slotConfig?.name || ''
const logContext = [
  `chapterId=${props.chapterId}`,
  `adOrder=${props.adOrder}`,
  `globalOrder=${globalPreloadOrder}`,
  `batch=${preloadBatch}`,
  `slot=${slotName}`,
  `unit=${unitPath}`,
  `div=${divId}`,
]
let loadObserver: IntersectionObserver | null = null
let reportObserver: IntersectionObserver | null = null
let reported = false
let filled = false
let visible = false
let shown = false
let disposed = false
let halfVisible = false
let failed = false
let failureFallbackTriggered = false
let initialScrollTop = 0
let scrollBaselineReady = false
let scrollContainer: HTMLElement | undefined

function getScrollTop() {
  return scrollContainer?.scrollTop || 0
}

function hasScrolledPastInitialPosition() {
  return scrollBaselineReady && Math.abs(getScrollTop() - initialScrollTop) >= 24
}

function maybeShowAd() {
  if (!filled || !visible)
    return
  showAd()
}

function reportVisible() {
  if (reported)
    return
  reported = true
  pushAxonEvent('purchase', {
    currency: 'USD',
    value: 0.001,
    transaction_id: `ad_gpt_${props.adSlotIndex}_${Date.now()}`,
    items: [{
      item_id: `ad_slot_gpt_${props.adSlotIndex}`,
      item_name: `GPT_Slot_${props.adSlotIndex}`,
      price: 0,
      quantity: 1,
    }],
  }, { is_taboola_ad_purchase: true })
  debug('可见性上报 %s', logContext.join(' '))
}

function advancePreloadAfterScroll() {
  if (shown && halfVisible && hasScrolledPastInitialPosition())
    noteAdVisible(globalPreloadOrder)
}

function handleContainerScroll() {
  advancePreloadAfterScroll()
  if (!failed || failureFallbackTriggered || !hasScrolledPastInitialPosition())
    return
  const el = containerRef.value
  if (!el)
    return
  const { top, bottom } = el.getBoundingClientRect()
  if (bottom <= 0 || top >= window.innerHeight)
    return

  failureFallbackTriggered = true
  debug('滚动到填充失败的广告，放开下一批 %s', logContext.join(' '))
  noteAdFailed(globalPreloadOrder)
}

function showAd() {
  if (shown)
    return
  shown = true
  const el = containerRef.value
  if (!el)
    return
  el.style.visibility = 'visible'
  el.style.maxHeight = 'none'
  el.style.minHeight = `${adSize[1]}px`
  el.style.overflow = 'visible'
  debug('广告展开展示 %s', logContext.join(' '))
  reportObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      halfVisible = entry.isIntersecting
      if (!entry.isIntersecting)
        continue
      reportVisible()
      // 首屏仅加载 #1/#2；必须发生实际滚动后才可继续扩批。
      advancePreloadAfterScroll()
    }
  }, { root: scrollContainer || null, threshold: 0.5 })
  reportObserver.observe(el)
}

onMounted(async () => {
  if (!isAdAvailable)
    return
  const el = containerRef.value
  if (!el)
    return
  scrollContainer = readerContainerRef?.value
  scrollContainer?.addEventListener('scroll', handleContainerScroll, { passive: true })
  await nextTick()
  requestAnimationFrame(() => {
    initialScrollTop = getScrollTop()
    scrollBaselineReady = true
  })

  loadObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting)
        continue
      loadObserver?.disconnect()
      loadObserver = null
      visible = true
      debug('广告位进入可见范围 filled=%s %s', filled, logContext.join(' '))
      // 仅首个进入视口的广告启动首批，避免隐藏占位容器连锁触发后续批次。
      startPreloadIfNeeded(globalPreloadOrder)
      maybeShowAd()
    }
  }, { root: scrollContainer || null, threshold: 0 })
  loadObserver.observe(el)

  await waitForPreloadTurn(globalPreloadOrder)
  if (disposed)
    return

  debug('窗口允许，开始预加载广告 %s', logContext.join(' '))
  filled = await displayAd(divId, unitPath, slotName)
  debug('预加载填充完成 filled=%s visible=%s %s', filled, visible, logContext.join(' '))
  if (!filled) {
    failed = true
    debug('广告填充失败，等待滚动到该位置后再放开下一批 %s', logContext.join(' '))
  }
  maybeShowAd()
})

onUnmounted(() => {
  disposed = true
  scrollContainer?.removeEventListener('scroll', handleContainerScroll)
  loadObserver?.disconnect()
  reportObserver?.disconnect()
  destroyAd(divId)
})
</script>

<template>
  <div
    v-if="isAdAvailable"
    :id="divId"
    ref="containerRef"
    class="chapter-ad-gpt"
  />
</template>

<style scoped>
.chapter-ad-gpt {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 1px;
  padding: 8px 0;
  visibility: hidden;
  max-height: 1px;
  overflow: hidden;
}
</style>
